import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../components/Messages';
import { showApiError } from '../../utils/errorUtils';
import { isAdmin as checkAdmin } from '../../utils/roleUtils';
import '../Admin/Users.css';
import { formatAppDate } from '../../utils/dateUtils';
import { INK_LOCATION, preferredLocation } from '../../utils/locations';

const ViewFormulationOrder = forwardRef(({
  orderId,
  editMode = false,
  createMode = false,
  onBack,
  onSave,
  onCancel,
  onOrderCreated,
  onStatusLoaded,
}, ref) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const { showSuccess, showError } = useMessages();

  const [form, setForm] = useState({
    recipe_id: '',
    batch_number: '',
    batch_multiplier: '1',
    planned_output_quantity: '',
    output_item_id: '',
    destination_location: '',
    notes: '',
  });
  const [orderData, setOrderData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showCompleteMode, setShowCompleteMode] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  // Lookups
  const [recipes, setRecipes] = useState([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [locations, setLocations] = useState([]);
  const [actualOutputQty, setActualOutputQty] = useState('');

  useImperativeHandle(ref, () => ({
    handleSave,
  }));

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (orderId && !createMode) {
      fetchOrder();
    }
    if (createMode) {
      fetchRecipes();
    }
  }, [orderId, createMode]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(getApiUrl(`/api/formulations/orders/${orderId}`));
      if (res.ok) {
        const data = await res.json();
        setOrderData(data);
        setForm({
          recipe_id: data.recipe_id || '',
          batch_number: data.batch_number || '',
          batch_multiplier: data.batch_multiplier || '1',
          planned_output_quantity: data.planned_output_quantity || '',
          output_item_id: data.output_item_id || '',
          destination_location: data.destination_location || '',
          notes: data.notes || '',
        });
        setItems((data.items || []).map(item => ({
          ...item,
          actual_quantity: item.actual_quantity || item.planned_quantity || '',
          waste_quantity: item.waste_quantity || '0',
        })));
        setActualOutputQty(data.actual_output_quantity || data.planned_output_quantity || '');
        if (onStatusLoaded) onStatusLoaded(data.status);
      }
    } catch (e) {
      console.error('Error fetching order:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipes = async () => {
    try {
      const res = await apiFetch(getApiUrl('/api/formulations/recipes?status=ACTIVE&page_size=100'));
      if (res.ok) {
        const data = await res.json();
        setRecipes(data.recipes || []);
      }
    } catch (e) {
      console.error('Error fetching recipes:', e);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await apiFetch(getApiUrl('/api/warehouse-locations?is_active=true&limit=100'));
      if (res.ok) {
        const data = await res.json();
        const filtered = data.filter(loc =>
          ['BULK', 'PALLET', 'SHELF', 'STAGING'].includes(loc.location_type)
        );
        setLocations(filtered);
        // Formulations make ink: preselect the ink warehouse (BT-001), else the first location.
        // The location shown is the one sent unless the user picks another
        setForm(prev => (prev.destination_location || !filtered.length
          ? prev
          : { ...prev, destination_location: preferredLocation(filtered, INK_LOCATION) }));
      } else {
        setLocations([]);
      }
    } catch (e) {
      setLocations([]);
    }
  };

  const handleRecipeSelect = (recipeId) => {
    const recipe = recipes.find(r => r.recipe_id === parseInt(recipeId));
    if (recipe) {
      const multiplier = parseFloat(form.batch_multiplier) || 1;
      setForm(prev => ({
        ...prev,
        recipe_id: recipe.recipe_id,
        output_item_id: recipe.output_item_id,
        planned_output_quantity: (parseFloat(recipe.output_quantity) * multiplier).toFixed(4),
      }));
      setItems((recipe.items || []).map(item => ({
        inventory_item_id: item.inventory_item_id,
        item_code: item.item_code,
        item_name: item.item_name,
        planned_quantity: (parseFloat(item.quantity) * multiplier).toFixed(4),
        actual_quantity: (parseFloat(item.quantity) * multiplier).toFixed(4),
        unit_cost: item.unit_cost || 0,
        unit_of_measure: item.unit_of_measure || 'KG',
        waste_quantity: '0',
        available_quantity: item.available_quantity || 0,
      })));
    }
  };

  const handleMultiplierChange = (value) => {
    const multiplier = parseFloat(value) || 1;
    setForm(prev => ({ ...prev, batch_multiplier: value }));

    const recipe = recipes.find(r => r.recipe_id === parseInt(form.recipe_id));
    if (recipe) {
      setForm(prev => ({
        ...prev,
        batch_multiplier: value,
        planned_output_quantity: (parseFloat(recipe.output_quantity) * multiplier).toFixed(4),
      }));
      setItems(prev => prev.map((item, idx) => {
        const recipeItem = recipe.items?.[idx];
        const planned = recipeItem ? (parseFloat(recipeItem.quantity) * multiplier).toFixed(4) : item.planned_quantity;
        return { ...item, planned_quantity: planned, actual_quantity: planned };
      }));
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        recipe_id: parseInt(form.recipe_id),
        batch_number: form.batch_number || null,
        batch_multiplier: parseFloat(form.batch_multiplier),
        planned_output_quantity: parseFloat(form.planned_output_quantity),
        output_item_id: parseInt(form.output_item_id),
        destination_location: form.destination_location,
        notes: form.notes || null,
      };

      let res;
      if (createMode) {
        res = await apiFetch(getApiUrl('/api/formulations/orders'), {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else {
        res = await apiFetch(getApiUrl(`/api/formulations/orders/${orderId}`), {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        const data = await res.json();
        showSuccess(createMode ? t('viewFormulationOrder.toasts.created') : t('viewFormulationOrder.toasts.updated'));
        if (createMode && onOrderCreated) {
          onOrderCreated(data.formulation_order_id);
        } else if (onSave) {
          onSave();
        }
      } else {
        await showApiError(res, showError, tCommon);
        return;
      }
    } catch (e) {
      showError(t('viewFormulationOrder.toasts.saveError'));
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      const payload = {
        actual_output_quantity: parseFloat(actualOutputQty),
        performed_by: user?.user_id || 1,
        items: items.map(item => ({
          order_item_id: item.order_item_id,
          actual_quantity: parseFloat(item.actual_quantity),
          waste_quantity: parseFloat(item.waste_quantity || 0),
        })),
      };

      const res = await apiFetch(getApiUrl(`/api/formulations/orders/${orderId}/complete`), {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        showSuccess(t('viewFormulationOrder.toasts.completed'));
        setShowCompleteMode(false);
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewFormulationOrder.toasts.completeError'));
      console.error(e);
    } finally {
      setCompleting(false);
    }
  };

  const handleStart = async () => {
    try {
      const res = await apiFetch(getApiUrl(`/api/formulations/orders/${orderId}/start`), {
        method: 'POST',
      });
      if (res.ok) {
        showSuccess(t('viewFormulationOrder.toasts.started'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewFormulationOrder.toasts.startError'));
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('viewFormulationOrder.confirm.cancel'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/formulations/orders/${orderId}/cancel`), {
        method: 'POST',
      });
      if (res.ok) {
        showSuccess(t('viewFormulationOrder.toasts.cancelled'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewFormulationOrder.toasts.cancelError'));
    }
  };

  const handleVoid = async () => {
    if (voiding || voidReason.trim().length < 5) return;
    setVoiding(true);
    try {
      const res = await apiFetch(getApiUrl(`/api/formulations/orders/${orderId}/void`), {
        method: 'POST',
        body: JSON.stringify({ reason: voidReason.trim() }),
      });
      if (res.ok) {
        showSuccess(t('viewFormulationOrder.toasts.voided'));
        setShowVoidModal(false);
        setVoidReason('');
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewFormulationOrder.toasts.voidError'));
      console.error(e);
    } finally {
      setVoiding(false);
    }
  };

  const updateItemActual = (idx, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const formatCurrency = (val) => val != null ? `$${parseFloat(val).toFixed(2)}` : '-';
  const formatQty = (val) => val != null ? parseFloat(val).toFixed(4) : '-';

  const calcItemCost = (item) => {
    const qty = parseFloat(item.actual_quantity) || 0;
    const cost = parseFloat(item.unit_cost) || 0;
    return qty * cost;
  };

  const totalInputCost = items.reduce((sum, item) => sum + calcItemCost(item), 0);
  const costPerUnit = parseFloat(actualOutputQty) > 0 ? totalInputCost / parseFloat(actualOutputQty) : 0;

  const isEditing = editMode || createMode;
  const status = orderData?.status || 'DRAFT';
  const canStart = status === 'DRAFT';
  const canComplete = status === 'DRAFT' || status === 'IN_PROGRESS';
  const canCancel = status === 'DRAFT' || status === 'IN_PROGRESS';
  const isAdmin = checkAdmin(user);
  const canVoid = status === 'COMPLETED' && isAdmin;

  if (loading) {
    return <div className="view-container"><div className="loading">{t('viewFormulationOrder.loading')}</div></div>;
  }

  return (
    <div className="view-container" style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2>{createMode ? t('viewFormulationOrder.newTitle') : orderData?.order_number || t('viewFormulationOrder.order')}</h2>
        {!isEditing && !showCompleteMode && orderId && (
          <div style={{ display: 'flex', gap: '6px' }}>
            {canStart && (
              <button className="btn-secondary" onClick={handleStart}>
                ▶ {t('viewFormulationOrder.actions.start')}
              </button>
            )}
            {canComplete && (
              <button className="btn-primary" onClick={() => setShowCompleteMode(true)}>
                ✅ {t('viewFormulationOrder.actions.complete')}
              </button>
            )}
            {canCancel && (
              <button className="btn-danger" onClick={handleCancel}>
                ✖ {t('common:ui.cancel')}
              </button>
            )}
            {canVoid && (
              <button className="btn-danger" onClick={() => setShowVoidModal(true)}>
                {t('viewFormulationOrder.actions.voidOrder')}
              </button>
            )}
          </div>
        )}
        {showCompleteMode && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="btn-primary" onClick={handleComplete} disabled={completing}>
              {completing ? t('viewFormulationOrder.actions.processing') : `✅ ${t('viewFormulationOrder.actions.confirmComplete')}`}
            </button>
            <button className="btn-secondary" onClick={() => setShowCompleteMode(false)}>
              ← {t('common:ui.back')}
            </button>
          </div>
        )}
      </div>

      {/* Order Header Fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
        {createMode ? (
          <>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.recipe')}</label>
              <input
                list="recipe-list"
                value={recipeSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setRecipeSearch(val);
                  const match = recipes.find(r => `${r.recipe_code} - ${r.recipe_name}` === val);
                  if (match) handleRecipeSelect(match.recipe_id);
                }}
                placeholder={t('viewFormulationOrder.fields.searchRecipe')}
              />
              <datalist id="recipe-list">
                {recipes.map(r => (
                  <option key={r.recipe_id} value={`${r.recipe_code} - ${r.recipe_name}`} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.batchMultiplier')}</label>
              <input type="number" step="0.01" min="0.01" value={form.batch_multiplier} onChange={(e) => handleMultiplierChange(e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.plannedOutput')}</label>
              <input type="number" step="0.0001" value={form.planned_output_quantity} onChange={(e) => setForm(prev => ({ ...prev, planned_output_quantity: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.batchNumber')}</label>
              <input type="text" value={form.batch_number} onChange={(e) => setForm(prev => ({ ...prev, batch_number: e.target.value }))} placeholder={t('common:ui.optional')} />
            </div>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.location')}</label>
              <select
                value={form.destination_location}
                onChange={(e) => setForm(prev => ({ ...prev, destination_location: e.target.value }))}
              >
                {locations.map(loc => (
                  <option key={loc.location_code} value={loc.location_code}>
                    {loc.location_code} — {loc.location_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('common:ui.notes')}</label>
              <input type="text" value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder={t('common:ui.optional')} />
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.orderNumber')}</label>
              <div className="field-value"><strong>{orderData?.order_number}</strong></div>
            </div>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.recipe')}</label>
              <div className="field-value">{orderData?.recipe_code} - {orderData?.recipe_name}</div>
            </div>
            <div className="form-group">
              <label>{t('common:ui.status')}</label>
              <div className="field-value">
                <span className={`status-badge ${
                  status === 'COMPLETED' ? 'status-success' :
                  status === 'IN_PROGRESS' ? 'status-active' :
                  status === 'CANCELLED' || status === 'VOIDED' ? 'status-error' : 'status-warning'
                }`}>
                  {t('formulationOrders.status.' + status, { defaultValue: status })}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.outputItem')}</label>
              <div className="field-value">{orderData?.output_item_code} - {orderData?.output_item_name}</div>
            </div>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.batchMultiplier')}</label>
              <div className="field-value">{orderData?.batch_multiplier}x</div>
            </div>
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.plannedQty')}</label>
              <div className="field-value">{formatQty(orderData?.planned_output_quantity)}</div>
            </div>
            {(status === 'COMPLETED' || status === 'VOIDED') && (
              <>
                <div className="form-group">
                  <label>{t('viewFormulationOrder.fields.actualQty')}</label>
                  <div className="field-value"><strong>{formatQty(orderData?.actual_output_quantity)}</strong></div>
                </div>
                <div className="form-group">
                  <label>{t('viewFormulationOrder.fields.totalCost')}</label>
                  <div className="field-value"><strong>{formatCurrency(orderData?.total_cost)}</strong></div>
                </div>
                <div className="form-group">
                  <label>{t('viewFormulationOrder.fields.completed')}</label>
                  <div className="field-value">{orderData?.completed_date ? formatAppDate(orderData.completed_date, 'datetime') : '-'}</div>
                </div>
              </>
            )}
            {status === 'VOIDED' && (
              <>
                <div className="form-group">
                  <label>{t('viewFormulationOrder.fields.voidReason')}</label>
                  <div className="field-value" style={{ color: '#d32f2f' }}>{orderData?.void_reason}</div>
                </div>
                <div className="form-group">
                  <label>{t('viewFormulationOrder.fields.voidedBy')}</label>
                  <div className="field-value">{orderData?.voided_by}</div>
                </div>
                <div className="form-group">
                  <label>{t('viewFormulationOrder.fields.voidedDate')}</label>
                  <div className="field-value">{orderData?.voided_date ? formatAppDate(orderData.voided_date, 'datetime') : '-'}</div>
                </div>
              </>
            )}
            {orderData?.batch_number && (
              <div className="form-group">
                <label>{t('viewFormulationOrder.fields.batchNumber')}</label>
                <div className="field-value">{orderData.batch_number}</div>
              </div>
            )}
            <div className="form-group">
              <label>{t('viewFormulationOrder.fields.location')}</label>
              <div className="field-value">{orderData?.destination_location || '-'}</div>
            </div>
            {orderData?.performed_by_name && (
              <div className="form-group">
                <label>{t('viewFormulationOrder.fields.performedBy')}</label>
                <div className="field-value">{orderData.performed_by_name}</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Complete mode: editable actual quantities */}
      {showCompleteMode && (
        <div style={{ padding: '12px 16px', background: '#e8f5e9', borderRadius: '8px', marginBottom: '16px', border: '1px solid #4caf50' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
            <strong>{t('viewFormulationOrder.complete.actualOutput')}:</strong>
            <input
              type="number"
              step="0.0001"
              value={actualOutputQty}
              onChange={(e) => setActualOutputQty(e.target.value)}
              style={{ width: '140px' }}
            />
            <span style={{ color: '#666' }}>{t('viewFormulationOrder.complete.costPerUnit')}: {formatCurrency(costPerUnit)}</span>
            <span style={{ color: '#666' }}>{t('viewFormulationOrder.complete.totalInputCost')}: <strong>{formatCurrency(totalInputCost)}</strong></span>
          </div>
        </div>
      )}

      {/* Items Table */}
      <h3 style={{ marginBottom: '8px' }}>
        {showCompleteMode ? t('viewFormulationOrder.items.consumptionTitle') : t('viewFormulationOrder.items.title')}
      </h3>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('common:ui.material')}</th>
              <th>{t('viewFormulationOrder.fields.plannedQty')}</th>
              {(showCompleteMode || status === 'COMPLETED' || status === 'VOIDED') && <th>{t('viewFormulationOrder.fields.actualQty')}</th>}
              {(showCompleteMode || status === 'COMPLETED' || status === 'VOIDED') && <th>{t('viewFormulationOrder.items.waste')}</th>}
              <th>{t('viewFormulationOrder.items.available')}</th>
              <th>{t('viewFormulationOrder.items.unitCost')}</th>
              <th>{t('viewFormulationOrder.fields.totalCost')}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={showCompleteMode || status === 'COMPLETED' || status === 'VOIDED' ? 7 : 5} className="no-data">{t('viewFormulationOrder.items.empty')}</td></tr>
            ) : (
              items.map((item, idx) => {
                const hasStock = parseFloat(item.available_quantity || 0) >= parseFloat(item.actual_quantity || item.planned_quantity || 0);
                return (
                  <tr key={idx}>
                    <td>
                      <strong>{item.item_code}</strong>
                      <br/><span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{item.item_name}</span>
                    </td>
                    <td>{formatQty(item.planned_quantity)}</td>
                    {showCompleteMode && (
                      <td>
                        <input
                          type="number"
                          step="0.0001"
                          value={item.actual_quantity}
                          onChange={(e) => updateItemActual(idx, 'actual_quantity', e.target.value)}
                          style={{ width: '100px', borderColor: hasStock ? undefined : '#f44336' }}
                        />
                      </td>
                    )}
                    {!showCompleteMode && (status === 'COMPLETED' || status === 'VOIDED') && (
                      <td>{formatQty(item.actual_quantity)}</td>
                    )}
                    {showCompleteMode && (
                      <td>
                        <input
                          type="number"
                          step="0.0001"
                          value={item.waste_quantity}
                          onChange={(e) => updateItemActual(idx, 'waste_quantity', e.target.value)}
                          style={{ width: '80px' }}
                        />
                      </td>
                    )}
                    {!showCompleteMode && (status === 'COMPLETED' || status === 'VOIDED') && (
                      <td>{formatQty(item.waste_quantity)}</td>
                    )}
                    <td style={{ color: hasStock ? 'inherit' : '#f44336' }}>
                      {formatQty(item.available_quantity)}
                      {!hasStock && <span title={t('viewFormulationOrder.items.insufficientStock')}> ⚠️</span>}
                    </td>
                    <td>{formatCurrency(item.unit_cost)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>{formatCurrency(calcItemCost(item))}</strong>
                    </td>
                  </tr>
                );
              })
            )}
            {items.length > 0 && (
              <tr style={{ fontWeight: 'bold', background: 'var(--bg-secondary)' }}>
                <td colSpan={showCompleteMode || status === 'COMPLETED' || status === 'VOIDED' ? 6 : 4} style={{ textAlign: 'right' }}>{t('common:ui.total')}:</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(totalInputCost)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {orderData?.notes && !createMode && (
        <div style={{ marginTop: '12px', padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
          <label style={{ fontWeight: '600', fontSize: '12px' }}>{t('common:ui.notes')}</label>
          <p style={{ margin: '4px 0 0', fontSize: '13px' }}>{orderData.notes}</p>
        </div>
      )}

      {/* Void Confirmation Modal */}
      {showVoidModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--bg-primary, #fff)', borderRadius: '12px', padding: '24px',
            width: '480px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            <h3 style={{ margin: '0 0 12px', color: '#d32f2f' }}>{t('viewFormulationOrder.void.title')}</h3>
            <p style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
              {t('viewFormulationOrder.void.intro')}
            </p>
            <ul style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-secondary)', paddingLeft: '20px' }}>
              <li>{t('viewFormulationOrder.void.inputsReturned')}</li>
              <li>{t('viewFormulationOrder.void.outputRemoved')}</li>
              <li>{t('viewFormulationOrder.void.avgCostNotAdjusted')}</li>
            </ul>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label>{t('viewFormulationOrder.void.reasonLabel')}</label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder={t('viewFormulationOrder.void.reasonPlaceholder')}
                rows={3}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => { setShowVoidModal(false); setVoidReason(''); }}
                disabled={voiding}
              >
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleVoid}
                disabled={voiding || voidReason.trim().length < 5}
              >
                {voiding ? t('viewFormulationOrder.void.voiding') : t('viewFormulationOrder.void.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ViewFormulationOrder;
